import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MathfieldElement } from "mathlive";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const QUESTIONS = [
  {
    "id": "q1",
    "instruction": "An L-shaped garden is shown.\nFind its area.",
    "points": [
      [
        0,
        0
      ],
      [
        3,
        0
      ],
      [
        3,
        4
      ],
      [
        8,
        4
      ],
      [
        8,
        6
      ],
      [
        0,
        6
      ]
    ],
    "answers": [
      "28"
    ],
    "displayAnswer": "28\\,\\text{m²}",
    "unit": "m²",
    "latex": "",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "split": 2,
    "steps": [
      {
        "title": "Find the missing lengths",
        "hint": "For the horizontal box, subtract the width of the upright from the total width. For the vertical box, subtract the height of the bottom strip from the total height.",
        "fields": [
          [
            "x",
            "Missing horizontal length (m)",
            5
          ],
          [
            "y",
            "Missing vertical length (m)",
            4
          ]
        ]
      },
      {
        "title": "Calculate the two rectangle areas",
        "hint": "Rectangle A is the upper upright: multiply 3 by the missing vertical length you found. Rectangle B is the full bottom strip: 8 × 2. Multiply length by width for each.",
        "fields": [
          [
            "a",
            "Area of A (m²)",
            12
          ],
          [
            "b",
            "Area of B (m²)",
            16
          ]
        ]
      },
      {
        "title": "Combine the two areas",
        "hint": "The rectangles meet without overlapping. Add their areas to find the whole garden area.",
        "fields": [
          [
            "final",
            "Total area (m²)",
            28
          ]
        ]
      }
    ],
    "diagramBoxes": true
  },
  {
    "id": "q2",
    "instruction": "A gardener puts edging around the entire boundary of this L-shaped flower bed.\nHow many metres of edging are needed?",
    "points": [
      [
        0,
        0
      ],
      [
        4,
        0
      ],
      [
        4,
        5
      ],
      [
        10,
        5
      ],
      [
        10,
        8
      ],
      [
        0,
        8
      ]
    ],
    "answers": [
      "36"
    ],
    "displayAnswer": "36\\,\\text{m}",
    "unit": "m",
    "latex": "",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "steps": [
      {
        "title": "Find both missing boundary lengths",
        "hint": "For the horizontal box, subtract 4 from the full width. For the vertical box, subtract 3 from the full height.",
        "fields": [
          [
            "x",
            "Missing horizontal length (m)",
            6
          ],
          [
            "y",
            "Missing vertical length (m)",
            5
          ]
        ]
      },
      {
        "title": "Calculate the edging needed",
        "hint": "Add all six boundary lengths: 4 + 5 + 6 + 3 + 10 + 8. Edging follows the boundary.",
        "fields": [
          [
            "final",
            "Edging needed (m)",
            36
          ]
        ]
      }
    ],
    "diagramBoxes": true
  },
  {
    "id": "q3",
    "instruction": "A classroom floor is shown below.\nThe floor needs carpet.\nHow many square metres of carpet are needed?",
    "points": [
      [
        0,
        0
      ],
      [
        4,
        0
      ],
      [
        4,
        4
      ],
      [
        9,
        4
      ],
      [
        9,
        7
      ],
      [
        0,
        7
      ]
    ],
    "answers": [
      "43"
    ],
    "displayAnswer": "43\\,\\text{m²}",
    "unit": "m²",
    "latex": "",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "diagramBoxes": true
  },
  {
    "id": "q4",
    "instruction": "A playground is shown below.\nA fence is needed around its entire boundary.\nFind the total length of fencing in metres.",
    "points": [
      [
        0,
        0
      ],
      [
        12,
        0
      ],
      [
        12,
        3
      ],
      [
        8,
        3
      ],
      [
        8,
        9
      ],
      [
        4,
        9
      ],
      [
        4,
        3
      ],
      [
        0,
        3
      ]
    ],
    "answers": [
      "42"
    ],
    "displayAnswer": "42\\,\\text{m}",
    "unit": "m",
    "latex": "",
    "hidden": {
      "3": "y",
      "4": "x"
    },
    "diagramBoxes": true
  },
  {
    "id": "q5",
    "instruction": "A lawn is shown below.\nThe lawn is to be fertilised. One bag treats 10 m².\nWhat is the smallest whole number of bags needed?",
    "points": [
      [
        0,
        0
      ],
      [
        14,
        0
      ],
      [
        14,
        3
      ],
      [
        9,
        3
      ],
      [
        9,
        10
      ],
      [
        5,
        10
      ],
      [
        5,
        3
      ],
      [
        0,
        3
      ]
    ],
    "answers": [
      "7"
    ],
    "displayAnswer": "7\\,\\text{bags}",
    "unit": "bags",
    "latex": "",
    "hidden": {
      "3": "y",
      "4": "x"
    },
    "diagramBoxes": false
  },
  {
    "id": "q6",
    "instruction": "A patio is shown below.\nThe patio is to be paved. Paving costs £18 per square metre.\nFind the total cost in pounds.",
    "points": [
      [
        0,
        0
      ],
      [
        3,
        0
      ],
      [
        3,
        6
      ],
      [
        9,
        6
      ],
      [
        9,
        0
      ],
      [
        12,
        0
      ],
      [
        12,
        10
      ],
      [
        0,
        10
      ]
    ],
    "answers": [
      "1512"
    ],
    "displayAnswer": "\\pounds 1512",
    "unit": "£",
    "latex": "",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "diagramBoxes": false,
    "calculator": "yes"
  },
  {
    "id": "q7",
    "instruction": "A display board is shown below.\nA ribbon runs around its boundary, with a 0.5 m gap left for a wall bracket.\nHow many metres of ribbon are needed?",
    "points": [
      [
        0,
        0
      ],
      [
        2.5,
        0
      ],
      [
        2.5,
        3.5
      ],
      [
        7.5,
        3.5
      ],
      [
        7.5,
        5
      ],
      [
        0,
        5
      ]
    ],
    "answers": [
      "24.5"
    ],
    "displayAnswer": "24.5\\,\\text{m}",
    "unit": "m",
    "latex": "",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "diagramBoxes": false
  },
  {
    "id": "q8",
    "instruction": "A garden is shown below.\nThere is a 2 m gate along its boundary. Fencing costs £12.50 per metre.\nFind the cost of fencing the remaining boundary, in pounds.",
    "points": [
      [
        0,
        0
      ],
      [
        16,
        0
      ],
      [
        16,
        4
      ],
      [
        11,
        4
      ],
      [
        11,
        12
      ],
      [
        5,
        12
      ],
      [
        5,
        4
      ],
      [
        0,
        4
      ]
    ],
    "answers": [
      "675"
    ],
    "displayAnswer": "\\pounds 675",
    "unit": "£",
    "latex": "",
    "calculator": "yes",
    "hidden": {
      "3": "y",
      "4": "x"
    },
    "diagramBoxes": false
  },
  {
    "id": "q9",
    "instruction": "A garden is shown below.\nA 2 m by 3 m pond lies entirely inside its bottom strip. Grass seed covers 8 m² per box.\nWhat is the smallest whole number of boxes needed for the remaining garden?",
    "points": [
      [
        0,
        0
      ],
      [
        3,
        0
      ],
      [
        3,
        8
      ],
      [
        12,
        8
      ],
      [
        12,
        0
      ],
      [
        15,
        0
      ],
      [
        15,
        12
      ],
      [
        0,
        12
      ]
    ],
    "answers": [
      "13"
    ],
    "displayAnswer": "13\\,\\text{boxes}",
    "unit": "boxes",
    "latex": "",
    "calculator": "yes",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "diagramBoxes": false
  },
  {
    "id": "q10",
    "instruction": "A courtyard is shown below.\nA 2 m by 4 m flower bed lies entirely inside its bottom strip.\nThe rest is paved at £24 per m².\nA railing is fitted around the entire courtyard boundary at £15 per metre.\nNo railing is needed around the flower bed.\nFind the combined cost in pounds.",
    "points": [
      [
        0,
        0
      ],
      [
        4,
        0
      ],
      [
        4,
        9
      ],
      [
        14,
        9
      ],
      [
        14,
        0
      ],
      [
        18,
        0
      ],
      [
        18,
        14
      ],
      [
        0,
        14
      ]
    ],
    "answers": [
      "4926"
    ],
    "displayAnswer": "\\pounds 4926",
    "unit": "£",
    "latex": "",
    "calculator": "yes",
    "hidden": {
      "1": "y",
      "2": "x"
    },
    "diagramBoxes": false
  }
];

const STORAGE_KEY = "area_perimeter_compound_shapes_v1";
const SUBMIT_URL =
  `${process.env.REACT_APP_HOMEWORK_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "")}/api/homework/area-and-perimeter-of-compound-shapes`;

function normalize(raw) {
  let value = String(raw ?? "")
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
    .replace(/\\cdot|\\times/g, "*")
    .replace(/\\div/g, "/")
    .replace(/\\,/g, "")
    .replace(/\s+/g, "")
    .replace(/[{}()]/g, "");

  return value.replace(/\*/g, "");
}

function makeSubmissionId() {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ResultIcon({ type, size = 28 }) {
  const isCorrect = type === "correct";
  const colour = isCorrect ? "#138a5b" : "#d64545";
  const paleColour = isCorrect ? "#e7f7ef" : "#fdecec";

  return (
    <svg
      aria-label={isCorrect ? "Correct" : "Incorrect"}
      role="img"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="14" fill={paleColour} stroke={colour} strokeWidth="2" />
      {isCorrect ? (
        <path
          d="M9.2 16.2l4.3 4.4 9.5-10"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M11 11l10 10M21 11L11 21"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default function AreaAndPerimeterOfCompoundShapesQuiz() {
  const navigate = useNavigate();
  const questions = useMemo(() => QUESTIONS, []);

  const [studentName, setStudentName] = useState("");
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState(Array(questions.length).fill(null));
  const [studentAnswers, setStudentAnswers] = useState(Array(questions.length).fill(""));
  const [workingAnswers, setWorkingAnswers] = useState(Array(questions.length).fill(""));
  const [attemptHistory, setAttemptHistory] = useState(Array.from({ length: questions.length }, () => []));
  const [guidedProgress, setGuidedProgress] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [submissionError, setSubmissionError] = useState("");
  const [submissionId, setSubmissionId] = useState(makeSubmissionId);

  useEffect(() => {
    if (!customElements.get("math-field")) customElements.define("math-field", MathfieldElement);
  }, []);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("restart") === "1") {
        localStorage.removeItem(STORAGE_KEY);
        url.searchParams.delete("restart");
        window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
      }
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return;
      setStudentName(data.studentName || "");
      setNameConfirmed(Boolean(data.nameConfirmed));
      setCurrent(Number.isInteger(data.current) ? data.current : 0);
      if (Array.isArray(data.questionResults) && data.questionResults.length === questions.length) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers) && data.studentAnswers.length === questions.length) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers) && data.workingAnswers.length === questions.length) setWorkingAnswers(data.workingAnswers);
      if (Array.isArray(data.attemptHistory) && data.attemptHistory.length === questions.length) setAttemptHistory(data.attemptHistory);
      setGuidedProgress(data.guidedProgress || {});
      setDone(Boolean(data.done));
      setSubmissionStatus(data.submissionStatus || "idle");
      setSubmissionError(data.submissionError || "");
      setSubmissionId(data.submissionId || makeSubmissionId());
      const index = Number.isInteger(data.current) ? data.current : 0;
      setStudentAnswer(data.studentAnswers?.[index] || "");
      setWorking(data.workingAnswers?.[index] || "");
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [questions.length]);

  const saveProgress = (override = {}) => {
    const payload = {
      studentName, nameConfirmed, current, questionResults, studentAnswers,
      workingAnswers, attemptHistory, guidedProgress, done, submissionStatus, submissionError, submissionId, ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const confirmName = (event) => {
    event.preventDefault();
    const cleanName = studentName.trim();
    if (!cleanName) return;
    setStudentName(cleanName);
    setNameConfirmed(true);
    saveProgress({ studentName: cleanName, nameConfirmed: true });
  };

  const checkAnswer = () => {
    if (!studentAnswer.trim()) {
      setFeedback("Please enter an answer first.");
      return;
    }

    const q = questions[current];
    const isCorrect = q.answers.some((answer) => normalize(answer) === normalize(studentAnswer));
    void playQuizFeedback(isCorrect);
    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    updatedStudentAnswers[current] = studentAnswer;
    updatedWorkingAnswers[current] = working;
    updatedHistory[current].push(studentAnswer);

    const updatedResults = [...questionResults];
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);
    setAttemptHistory(updatedHistory);

    if (isCorrect) {
      updatedResults[current] = "correct";
      setQuestionResults(updatedResults);
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="correct" />
          <strong style={{ color: "#087f5b" }}>Correct!</strong>
        </div>
      );
    } else if (newAttempts >= 3) {
      updatedResults[current] = "incorrect";
      setQuestionResults(updatedResults);
      setFeedback("Correct answer shown in blue. You can move on now.");
    } else {
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="incorrect" />
          <strong style={{ color: "#c0392b" }}>
            Try again. Attempts left: {3 - newAttempts}
          </strong>
        </div>
      );
    }

    saveProgress({
      studentAnswers: updatedStudentAnswers,
      workingAnswers: updatedWorkingAnswers,
      attemptHistory: updatedHistory,
      questionResults: updatedResults,
    });
  };

  const isGuided = Boolean(questions[current].steps);
  const canAdvance = isGuided
    ? questionResults[current] === "correct"
    : questionResults[current] === "correct" || attempts >= 3;

  const updateGuidedProgress = (progress) => {
    const updated = { ...guidedProgress, [questions[current].id]: progress };
    setGuidedProgress(updated);
    saveProgress({ guidedProgress: updated });
  };

  const completeGuidedQuestion = (answer, progress) => {
    const updatedResults = [...questionResults];
    const updatedAnswers = [...studentAnswers];
    const updatedWorking = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    const updatedProgress = { ...guidedProgress, [questions[current].id]: progress };
    updatedResults[current] = "correct";
    updatedAnswers[current] = answer;
    updatedHistory[current].push(answer);
    updatedWorking[current] = questions[current].steps.flatMap(step => step.fields.map(([key, label]) => label + ": " + progress.values[key])).join("; ");
    setQuestionResults(updatedResults);
    setStudentAnswers(updatedAnswers);
    setWorkingAnswers(updatedWorking);
    setAttemptHistory(updatedHistory);
    setStudentAnswer(answer);
    setWorking(updatedWorking[current]);
    setGuidedProgress(updatedProgress);
    saveProgress({ questionResults: updatedResults, studentAnswers: updatedAnswers, workingAnswers: updatedWorking, attemptHistory: updatedHistory, guidedProgress: updatedProgress });
  };

  const submitResults = async (results, answers, histories, workings) => {
    setSubmissionError("");
    setSubmissionStatus("sending");
    saveProgress({ done: true, questionResults: results, studentAnswers: answers, attemptHistory: histories, workingAnswers: workings, submissionStatus: "sending" });

    const payload = {
      submissionId,
      studentName: studentName.trim(),
      quizTitle: "Area and Perimeter Of Compound Shapes",
      completedAt: new Date().toISOString(),
      score: results.filter((result) => result === "correct").length,
      total: questions.length,
      questions: questions.map((question, index) => ({
        number: index + 1,
        question: `${question.instruction} Boundary vertices in metres, clockwise: ${JSON.stringify(question.points)}. All corners are right angles.`,
        instruction: question.instruction,
        shape: { points: question.points, unit: "m" },
        correctAnswer: question.displayAnswer,
        studentAnswer: answers[index] || "No answer",
        attempts: guidedProgress[question.id]?.attempts?.length
          ? guidedProgress[question.id].attempts : histories[index] || [],
        working: workings[index] || "",
        result: results[index] || "incorrect",
      })),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const messages = {
          404: "The results service could not be found. Please let Ryan know.",
          503: "Email delivery is not configured yet. Please let Ryan know.",
          502: "The email service could not deliver your results. Please try again later.",
        };
        throw new Error(messages[response.status] || "The results service could not accept your results. Please try again later.");
      }
      setSubmissionStatus("sent");
      saveProgress({ done: true, submissionStatus: "sent", submissionError: "" });
    } catch (error) {
      const message = error instanceof TypeError
        ? "Cannot connect to the results service. Please let Ryan know or try again later."
        : error.message;
      setSubmissionError(message);
      setSubmissionStatus("failed");
      saveProgress({ done: true, submissionStatus: "failed", submissionError: message });
    }
  };

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);
    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
      return;
    }

    if (reviewMode) {
      navigate("/student-dashboard");
      return;
    }

    setDone(true);
    saveProgress({ done: true });
    if (submissionStatus !== "sent") {
      submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
    }
  };

  const previousQuestion = () => {
    if (current === 0) return;
    const previous = current - 1;
    setCurrent(previous);
    setFeedback(null);
    setAttempts(attemptHistory[previous]?.length || 0);
    setStudentAnswer(studentAnswers[previous] || "");
    setWorking(workingAnswers[previous] || "");
    saveProgress({ current: previous });
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrent(0);
    setFeedback(null);
    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");
  };

  const retrySubmission = () => submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
  const score = questionResults.filter((result) => result === "correct").length;

  const feedbackMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "🌟 Perfect—you got everything right!";
    if (percentage >= 80) return "🎉 Great job—you did really well!";
    if (percentage >= 50) return "👍 Good effort. Review any red questions.";
    return "💪 Keep practising. Reviewing your answers will help.";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((result, index) => (
        <div key={questions[index].id} style={{
          ...styles.progressDot,
          backgroundColor: result === "correct" ? "#00b894" : result === "incorrect" ? "#d63031" : "#636e72",
          border: index === current && !done ? "3px solid #f9b710" : "2px solid transparent",
        }}>
          {index + 1}
        </div>
      ))}
    </div>
  );

  if (!nameConfirmed) {
    return (
  
        <div style={styles.container}>
          <div style={styles.nameCard}>
            <h1 style={styles.heading}>Area and Perimeter Of Compound Shapes Quiz</h1>
            <p>Please enter your name before you begin. Your results will be sent to Ryan when you finish.</p>
            <form onSubmit={confirmName}>
              <label htmlFor="student-name" style={styles.label}>Your name</label>
              <input id="student-name" autoFocus autoComplete="name" maxLength={80} value={studentName}
                onChange={(event) => setStudentName(event.target.value)} style={styles.nameInput} />
              <button type="submit" disabled={!studentName.trim()} style={styles.startBtn}>Start Quiz ➡️</button>
            </form>
          </div>
        </div>
   
    );
  }

  const q = questions[current];
  const currentResult = questionResults[current];
  const completedFieldStyle =
    currentResult === "correct"
      ? styles.correctField
      : currentResult === "incorrect"
        ? styles.incorrectField
        : styles.unansweredField;

  return (
       <div style={styles.container}>
        <h1 style={styles.heading}>Area and Perimeter Of Compound Shapes</h1>
        <p style={styles.studentLabel}>Student: <strong>{studentName}</strong></p>
        {renderProgress()}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage()}</p>
            {submissionStatus === "sending" && <p>Sending your results to Ryan…</p>}
            {submissionStatus === "sent" && (
              <div style={{ ...styles.statusLine, ...styles.sentMessage }}>
                <ResultIcon type="correct" size={25} />
                <span>Your results have been sent to Ryan.</span>
              </div>
            )}
            {submissionStatus === "failed" && (
              <div>
                <p style={styles.errorMessage}>Your quiz is saved, but the results could not be sent.</p>
                {submissionError && <p role="alert">{submissionError}</p>}
                <button onClick={retrySubmission} style={styles.submitBtn}>Try Sending Again</button>
              </div>
            )}
            <button onClick={startReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>Question {current + 1} of {questions.length}</h3>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
              <p style={{ ...styles.instruction, flex: 1, margin: 0, whiteSpace: "pre-line" }}>
                {q.instruction}
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <CalculatorIcon allowed={q.calculator === "yes"} />
              </div>
            </div>
            {!isGuided && <CompoundShape key={q.id} question={q} progress={guidedProgress[q.id]} readOnly={reviewMode || canAdvance} onChange={updateGuidedProgress} />}
            {isGuided && (guidedProgress[q.id] || (!reviewMode && !canAdvance)) ? (
              <GuidedCompoundQuestion key={q.id} question={q} progress={guidedProgress[q.id]}
                readOnly={reviewMode || canAdvance} onChange={updateGuidedProgress} onComplete={completeGuidedQuestion} />
            ) : (
              <>


            <label style={styles.label}>Show your working:</label>
            <textarea rows={4} readOnly={reviewMode || canAdvance}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(event) => setWorking(event.target.value)}
              placeholder="Show any working here" style={{ ...styles.workingInput, ...completedFieldStyle }} />

            <label htmlFor="quiz-answer" style={styles.label}>Answer:</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
            <math-field id="quiz-answer" aria-label={`Answer in ${q.unit}`} readOnly={reviewMode || canAdvance} style={{ ...styles.mathInput, flex: 1, minWidth: 0, width: "auto", marginTop: 0, ...completedFieldStyle, ...(questionResults[current] === "incorrect" ? { color: "#1d4ed8", backgroundColor: "#eff6ff", borderColor: "#60a5fa" } : {}) }}
              value={questionResults[current] === "incorrect" ? q.answers[0] : reviewMode || canAdvance ? studentAnswers[current] : studentAnswer}
              onInput={(event) => !reviewMode && setStudentAnswer(event.target.value)} />
            <span style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1.25rem", color: "#334155", flexShrink: 0, order: q.unit === "£" ? -1 : 0 }}>{q.unit}</span>
            </div>

              </>
            )}

            {reviewMode && (
              <div style={styles.reviewAnswer}>
                <div style={styles.statusLine}>
                  <ResultIcon type={questionResults[current] === "correct" ? "correct" : "incorrect"} />
                  <strong style={{ color: questionResults[current] === "correct" ? "#087f5b" : "#c0392b" }}>
                    {questionResults[current] === "correct" ? "Correct:" : "Correct answer:"}
                  </strong>
                </div>
                <BlockMath math={q.displayAnswer} />
              </div>
            )}
            {!reviewMode && feedback && <div style={styles.feedbackBox}>{feedback}</div>}
            {!isGuided && !reviewMode && !canAdvance && <button onClick={checkAnswer} style={styles.submitBtn}>Check Answer</button>}

            {(canAdvance || reviewMode) && (
              <div>
                {current > 0 && <button onClick={previousQuestion} style={styles.secondaryBtn}>← Back</button>}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish Review" : "Finish & Send 🎉"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
  );
}

const styles = {
  container: { padding: "2rem", fontFamily: "'Fredoka', cursive", backgroundColor: "#eaf4fc", minHeight: "100vh" },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  studentLabel: { textAlign: "center", marginBottom: "1rem" },
  card: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 700, margin: "1rem auto 0" },
  nameCard: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 520, margin: "4rem auto" },
  nameInput: { display: "block", width: "100%", boxSizing: "border-box", padding: "0.75rem", margin: "0.5rem 0 1rem", border: "1px solid #aaa", borderRadius: 8, fontSize: "1.1rem" },
  startBtn: { padding: "0.7rem 1.3rem", backgroundColor: "#6c5ce7", color: "white", fontWeight: "bold", border: 0, borderRadius: 7, cursor: "pointer" },
  calculatorIndicator: { marginBottom: 12, display: "flex", justifyContent: "flex-end" },
  instruction: { fontWeight: 700, fontSize: "1.05rem", marginBottom: 24 },
  label: { fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "block" },
  mathInput: { display: "block", width: "100%", boxSizing: "border-box", fontSize: "1.25rem", padding: "0.65rem", borderRadius: 8, border: "1px solid #ccc", marginTop: 8 },
  workingInput: { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #ccc", padding: "0.75rem", fontSize: "1rem", fontFamily: "'Fredoka', cursive", marginTop: 8, marginBottom: 20 },
  unansweredField: { backgroundColor: "#fff", border: "1px solid #ccc" },
  correctField: { backgroundColor: "#e8f8ee", border: "2px solid #42a96b" },
  incorrectField: { backgroundColor: "#fdeaea", border: "2px solid #d95858" },
  feedbackBox: { marginTop: "1rem" },
  statusLine: { display: "flex", alignItems: "center", gap: 10 },
  reviewAnswer: { marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f6fa", borderRadius: 8 },
  submitBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#0984e3", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  nextBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#00b894", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
  secondaryBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  progressWrapper: { display: "flex", justifyContent: "center", gap: 10, marginBottom: "1.5rem", flexWrap: "wrap" },
  progressDot: { width: 36, height: 36, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold", color: "white" },
  resultsBox: { textAlign: "center", backgroundColor: "white", maxWidth: 700, margin: "2rem auto", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
  feedbackText: { fontSize: "1.25rem", margin: "1rem 0" },
  sentMessage: { color: "#087f5b", fontWeight: 700, justifyContent: "center", margin: "1rem 0" },
  errorMessage: { color: "#c0392b", fontWeight: 700 },
  reviewBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
};


function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}


function CompoundShape({ question, progress, readOnly = false, onChange }) {
  const p = question.points, w = Math.max(...p.map(v=>v[0])), h = Math.max(...p.map(v=>v[1]));
  const scale = Math.min(300/w, 245/h), ox = 95, oy = 55;
  const state = progress || { step: 0, values: {}, status: {} };
  return <figure style={{margin:'0 auto 24px',maxWidth:470}}>
    <svg aria-label={`Compound shape with ${question.diagramBoxes ? 'blank boxes' : 'unlabelled sides'} for missing side lengths; all lengths in metres`} viewBox={`0 0 ${w*scale+190} ${h*scale+110}`} style={{width:'100%',display:'block',fontFamily:'Inter, sans-serif'}}>
      <polygon points={p.map(([x,y])=>[ox+x*scale,oy+y*scale].join(',')).join(' ')} fill="#b9e8d4" stroke="#176b55" strokeWidth="3" />
      {question.id === "q10" && <g aria-label="Flower bed, 4 metres wide and 2 metres high, inside the bottom strip">
        <rect x={ox+7*scale} y={oy+10.5*scale} width={4*scale} height={2*scale} fill="#a1623b" stroke="#713f12" strokeWidth="2" />
        <text x={ox+9*scale} y={oy+11.5*scale} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontWeight="600" fill="#fff">Flower bed</text>
        <text x={ox+9*scale} y={oy+10.5*scale-7} textAnchor="middle" fontSize="12" fontWeight="600" fill="#17324d">4 m</text>
        <text x={ox+11*scale+8} y={oy+11.5*scale} dominantBaseline="middle" fontSize="12" fontWeight="600" fill="#17324d">2 m</text>
      </g>}
      {question.id === "q9" && <g aria-label="Pond, 3 metres wide and 2 metres high, inside the bottom strip">
        <rect x={ox+6*scale} y={oy+9*scale} width={3*scale} height={2*scale} fill="#bae6fd" stroke="#0284c7" strokeWidth="2" />
        <text x={ox+7.5*scale} y={oy+10*scale} textAnchor="middle" dominantBaseline="middle" fontSize="12" fontWeight="600" fill="#075985">Pond</text>
        <text x={ox+7.5*scale} y={oy+9*scale-7} textAnchor="middle" fontSize="12" fontWeight="600" fill="#17324d">3 m</text>
        <text x={ox+9*scale+8} y={oy+10*scale} dominantBaseline="middle" fontSize="12" fontWeight="600" fill="#17324d">2 m</text>
      </g>}
      {question.id === "q8" && <g aria-label="2 metre gate at the bottom centre">
        <line x1={ox+(w/2-1)*scale} x2={ox+(w/2+1)*scale} y1={oy+h*scale} y2={oy+h*scale} stroke="#fff" strokeWidth="7" />
        <line x1={ox+(w/2-1)*scale} x2={ox+(w/2+1)*scale} y1={oy+h*scale} y2={oy+h*scale} stroke="#92400e" strokeWidth="4" strokeDasharray="5 3" />
        {[-1, 1].map(side => <line key={side} x1={ox+(w/2+side)*scale} x2={ox+(w/2+side)*scale} y1={oy+h*scale-6} y2={oy+h*scale+6} stroke="#92400e" strokeWidth="3" />)}
        <text x={ox+w*scale/2} y={oy+h*scale+29} textAnchor="middle" fontSize="14" fontWeight="600" fill="#17324d">Gate</text>
      </g>}
      {question.split && <><line x1={ox} x2={ox+p[1][0]*scale} y1={oy+(h-question.split)*scale} y2={oy+(h-question.split)*scale} stroke="#6d28d9" strokeWidth="2" strokeDasharray="6 5"/><text x={ox+p[1][0]*scale/2} y={oy+(h-question.split)*scale/2} textAnchor="middle">A</text><text x={ox+w*scale/2} y={oy+(h-question.split/2)*scale+5} textAnchor="middle">B</text></>}
      {p.map(([x,y], i) => {
        const [nx,ny] = p[(i+1)%p.length], dx = nx-x, dy = ny-y, len = Math.abs(dx)+Math.abs(dy);
        const key = question.hidden?.[i];
        const hasBox = key && question.diagramBoxes;
        const cx = ox+(x+nx)*scale/2+(dy/len)*(hasBox ? 55 : 23);
        const cy = oy+(y+ny)*scale/2-(dx/len)*(hasBox ? 27 : 23);
        if (!key) return <text key={i} x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="600" fill="#17324d" stroke="white" strokeWidth="5" paintOrder="stroke">{Number(len.toFixed(4))} m</text>;
        if (!hasBox) return null;
        const status = state.status?.[key];
        const label = dx ? 'Missing horizontal length (m)' : 'Missing vertical length (m)';
        return <foreignObject key={i} x={cx-45} y={cy-18} width="90" height="36">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ display:'flex', alignItems:'center', gap:4, height:36, fontSize:14 }}>
            <input aria-label={label} inputMode="decimal" value={state.values?.[key] || ''} readOnly={readOnly || (question.steps && state.step > 0) || status === 'correct'} aria-invalid={status === 'incorrect'}
              onChange={event => onChange?.({ ...state, values: { ...state.values, [key]: event.target.value }, status: { ...state.status, [key]: undefined }, message: '' })}
              style={{ ...guidedStyles.input, width:65, height:32, padding:3, fontSize:16, ...(status === 'correct' ? guidedStyles.correct : status === 'incorrect' ? guidedStyles.incorrect : {}) }} />
            <span>m</span>
          </div>
        </foreignObject>;
      })}
    </svg>
  </figure>;
}

function GuidedCompoundQuestion({ question, progress, readOnly, onChange, onComplete }) {
  const [activeHint, setActiveHint] = useState(null);
  const hintDialog = useRef(null);
  const reduceMotion = useReducedMotion();
  const hints = question.steps.map(s => s.hint);
  const state = progress || { step: 0, values: {}, status: {} };
  useEffect(() => { const d=hintDialog.current; if(activeHint!==null && !d.open)d.showModal(); if(activeHint===null && d.open)d.close(); },[activeHint]);
  const heading = (step, text) => (
    <div style={guidedStyles.stepHeading}>
      <h4 style={{ margin: 0 }}>{text}</h4>
      <button type="button" className="compound-hint-button" aria-label={`Hint for ${text.slice(3).toLowerCase()}`}
        onClick={() => setActiveHint(step)} style={guidedStyles.hintButton} title="Show a hint">
        <svg aria-hidden="true" width="24" height="24" viewBox="0 -1 24 24" style={{ display: "block" }}>
          <path className="compound-hint-bulb" d="M9 17h6v-2c0-2 3-3 3-7a6 6 0 00-12 0c0 4 3 5 3 7z" fill="#fde68a" stroke="currentColor" strokeWidth="1.7" />
          <path d="M9 20h6m-5 2h4M12 0v1M2 8h2m16 0h2M4 2l2 2m12 0l2-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );

  const check = () => {
    if(readOnly || state.step >= question.steps.length) return;
    const fields=question.steps[state.step].fields, status={...state.status};
    fields.forEach(([key,,answer])=>{const value=String(state.values[key] || '').trim();status[key]=value!=='' && Number(value)===answer?'correct':'incorrect';});
    const correct=fields.every(([key])=>status[key]==='correct');
    void playQuizFeedback(correct);
    const attempt=question.steps[state.step].title+': '+fields.map(([key,label])=>label+' = '+(state.values[key]||'Blank')+' ('+status[key]+')').join('; ');
    const next={...state,status,step:state.step+(correct?1:0),attempts:[...(state.attempts||[]),attempt],message:correct?'Correct! This section is complete.':'Check the red answers and try again. Every answer must be correct to continue.'};
    onChange(next);
    if(correct && next.step===question.steps.length)onComplete(question.answers[0],next);
  };
  return <div>
    {question.steps.map((step,index)=>index<=state.step && <section key={index} style={{...guidedStyles.section,...(state.step>index?guidedStyles.correct:{})}}>
      {heading(index, (index+1)+'. '+step.title)}
      {index === 0 ? <><CompoundShape question={question} progress={state} readOnly={readOnly} onChange={onChange} /></> : step.fields.map(([key,label])=><label key={key} style={{display:'block',margin:'12px 0'}}>{label}: <input aria-label={label} inputMode="decimal" value={state.values[key] || ''} readOnly={readOnly || state.step>index || state.status[key]==='correct'} aria-invalid={state.status[key]==='incorrect'} onChange={e=>onChange({...state,values:{...state.values,[key]:e.target.value},status:{...state.status,[key]:undefined},message:''})} style={{...guidedStyles.input,...(state.status[key]==='correct'?guidedStyles.correct:state.status[key]==='incorrect'?guidedStyles.incorrect:{})}} /></label>)}
      {state.step>index?<p style={guidedStyles.success}>✓ Correct</p>:!readOnly && <button type="button" onClick={check} style={guidedStyles.button}>Check step {index+1}</button>}
    </section>)}
    <p role="status" aria-live="polite">{state.message}</p>
      <style>{`
        .compound-hint-dialog::backdrop { background: rgba(30, 27, 75, 0.38); }
        .compound-hint-button { color: #92400e; background: #fffbeb; transition: background-color 150ms ease; }
        .compound-hint-button:hover, .compound-hint-button:focus-visible { background: #fde68a; }
      `}</style>
      <dialog ref={hintDialog} className="compound-hint-dialog" aria-labelledby="compound-hint-title"
        onCancel={() => setActiveHint(null)} onClose={() => setActiveHint(null)}
        onClick={(event) => { if (event.target === event.currentTarget) setActiveHint(null); }}
        style={guidedStyles.hintDialog}>
        {activeHint !== null && <motion.div key={activeHint} initial={reduceMotion ? false : { scale: 0.45, opacity: 0, rotate: -5 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 320, damping: 19 }}
          style={guidedStyles.hintBubble}>
          <h3 id="compound-hint-title" style={{ color: "#6d28d9", margin: "0 0 12px" }}>A little hint!</h3>
          <p style={{ fontSize: "1.1rem", lineHeight: 1.6, margin: "0 0 20px" }}>{hints[activeHint]}</p>
          <button type="button" autoFocus onClick={() => setActiveHint(null)} style={guidedStyles.button}>Got it!</button>
          <span aria-hidden="true" style={guidedStyles.bubbleTail} />
        </motion.div>}
      </dialog>

  </div>;
}

const guidedStyles = {
  stepHeading: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 },
  hintButton: { display: "grid", placeItems: "center", padding: 0, boxSizing: "border-box", flexShrink: 0, width: 40, height: 40, borderRadius: "50%", border: "2px solid #f59e0b", cursor: "pointer", boxShadow: "0 3px 0 #fcd34d" },
  hintDialog: { border: 0, padding: 36, background: "transparent", maxWidth: 540, width: "calc(100% - 24px)", boxSizing: "border-box", overflow: "visible", fontFamily: "'Fredoka', cursive" },
  hintBubble: { position: "relative", padding: 28, border: "3px solid #7c3aed", borderRadius: "32px 32px 32px 8px", background: "#fffbeb", color: "#334155", boxShadow: "0 0 20px #c4b5fd, 0 0 44px rgba(167,139,250,0.7), 0 12px 0 rgba(109,40,217,0.18)" },
  bubbleTail: { position: "absolute", bottom: -17, left: 28, width: 26, height: 26, background: "#fffbeb", borderRight: "3px solid #7c3aed", borderBottom: "3px solid #7c3aed", transform: "rotate(45deg)" },
  section: { borderWidth: 2, borderStyle: "solid", borderColor: "#cbd5e1", borderRadius: 8, padding: 16, marginBottom: 16 },
  correct: { backgroundColor: "#e8f8ee", borderColor: "#42a96b" },
  incorrect: { backgroundColor: "#fdeaea", borderColor: "#d95858" },
  table: { borderCollapse: "collapse", width: "100%", border: "2px solid #334155", backgroundColor: "white", fontSize: "1.15rem" },
  cell: { border: "1px solid #334155", padding: "14px 12px", textAlign: "center" },
  input: { width: 70, maxWidth: "100%", boxSizing: "border-box", padding: 8, borderWidth: 2, borderStyle: "solid", borderColor: "#94a3b8", borderRadius: 5, fontSize: "1.1rem", textAlign: "center" },
  button: { display: "block", marginTop: 16, padding: "10px 16px", backgroundColor: "#0984e3", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: "bold" },
  success: { color: "#087f5b", fontWeight: "bold" },
};


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}
